import { SearchInput } from "@bridgesplit/ui";
import { isDisabledValue, LendingVaultExpanded, ParsedMarketInformationWithLtvInfo } from "@bridgesplit/abf-react";
import { StrategyDuration } from "@bridgesplit/abf-sdk";
import { bigIntLamportsToUiAmount, EMPTY_STRING, formatBigIntPercent, percentDecimalsToUi } from "@bridgesplit/utils";

import { useManagerViewContext } from "./ManagerViewContext";
import {
    CollateralAllocationForm,
    CollateralAmountsForm,
    initialCollateralAllocationForm,
    initialCollateralForm,
    initialPrincipalCapsForm,
    initialRateForm,
    PrincipalCapsForm,
    RateForm
} from "./type";
import { convertVaultExpandedToMintToRateInfo } from "./util";
import { getInitialDetailForm } from "./detail/util";
import { getInitialSettingsForm } from "./settings/util";

export enum FormType {
    COLLATERAL_FORM = "collateralForm",
    DETAIL_FORM = "detailForm",
    RATE_FORM = "rateForm",
    COLLATERAL_ALLOCATION_FORM = "collateralAllocationForm",
    SETTINGS_FORM = "settingsForm",
    PRINCIPAL_CAPS_FORM = "principalCapsForm"
}

function useFormSearch(formType: FormType) {
    const { collateralForm, collateralDraft, rateForm, rateDraft, collateralAllocationForm, allocationDraft } =
        useManagerViewContext();

    const form = (() => {
        switch (formType) {
            case FormType.COLLATERAL_FORM:
                return collateralForm;
            case FormType.RATE_FORM:
                return rateForm;
            case FormType.COLLATERAL_ALLOCATION_FORM:
                return collateralAllocationForm;
        }
    })();

    const setSearch = (() => {
        switch (formType) {
            case FormType.COLLATERAL_FORM:
                return (search: string) =>
                    collateralDraft.update((form) => {
                        form.search = search;
                    });
            case FormType.RATE_FORM:
                return (search: string) =>
                    rateDraft.update((form) => {
                        form.search = search;
                    });
            case FormType.COLLATERAL_ALLOCATION_FORM:
                return (search: string) =>
                    allocationDraft.update((form) => {
                        form.search = search;
                    });
        }
    })();

    return {
        form,
        setSearch
    };
}

export function SearchCollateral({ formType = FormType.COLLATERAL_FORM }: { formType?: FormType }) {
    const { form, setSearch } = useFormSearch(formType);

    if (!form) return null;

    return (
        <SearchInput
            fullWidth
            placeholder="Search by token or address"
            onChange={(e) => setSearch?.(e.target.value)}
            value={form.search}
        />
    );
}

function updateRateForm(
    vaultExpanded: LendingVaultExpanded | undefined,
    marketInfo: ParsedMarketInformationWithLtvInfo | undefined,
    strategyDurations: StrategyDuration[] | undefined
): Partial<RateForm> {
    const rateInfo = convertVaultExpandedToMintToRateInfo(vaultExpanded, marketInfo, strategyDurations);
    if (!rateInfo) return { mintToRateInfo: {} };
    return { mintToRateInfo: rateInfo };
}

// This function is used to update the collateral allocation form. Max allocation is stored as BN (CBPS) in vault.
// Form stores percent string (e.g. "10.5"). Empty string = disabled.
function updateCollateralAllocationForm(
    marketInfo: ParsedMarketInformationWithLtvInfo | undefined
): Partial<CollateralAllocationForm> {
    if (!marketInfo?.marketInformationAssetInfo) {
        return { mintToMaxAllocationPct: {} };
    }

    const mintToMaxAllocationPct: Record<string, string> = {};

    for (const asset of marketInfo.marketInformationAssetInfo) {
        const mint = asset.assetIdentifier;
        const maxAllocationPctBn = asset.maxCollateralAllocationPct;
        if (isDisabledValue(maxAllocationPctBn)) {
            mintToMaxAllocationPct[mint] = EMPTY_STRING;
            continue;
        }
        mintToMaxAllocationPct[mint] = formatBigIntPercent(maxAllocationPctBn, { hideSymbol: true });
    }

    return { mintToMaxAllocationPct };
}

function updatePrincipalCaps(vaultExpanded: LendingVaultExpanded | undefined): Partial<PrincipalCapsForm> {
    const getCapValue = (cap?: bigint): string => {
        if (cap === undefined || isDisabledValue(cap)) {
            return EMPTY_STRING;
        }

        return bigIntLamportsToUiAmount(cap, vaultExpanded?.principalMetadata?.decimals);
    };
    const borrowCaps = vaultExpanded?.vaultStrategy?.terms.borrowCaps;
    const supplyCaps = vaultExpanded?.vaultStrategy?.terms.supplyCaps;
    const withdrawCaps = vaultExpanded?.vaultStrategy?.terms.withdrawCaps;

    return {
        max1hrBorrowCap: getCapValue(borrowCaps?.max1hr),
        max24hrBorrowCap: getCapValue(borrowCaps?.max24hr),
        maxOutstandingBorrowCap: getCapValue(borrowCaps?.maxOutstanding),
        max1hrSupplyCap: getCapValue(supplyCaps?.max1hr),
        max24hrSupplyCap: getCapValue(supplyCaps?.max24hr),
        maxOutstandingSupplyCap: getCapValue(supplyCaps?.maxOutstanding),
        max1hrWithdrawCap: getCapValue(withdrawCaps?.max1hr),
        max24hrWithdrawCap: getCapValue(withdrawCaps?.max24hr)
    };
}

function updateCollateralForm(marketInfo: ParsedMarketInformationWithLtvInfo | undefined) {
    //For all market info, make mintToInfo
    const mintToInfo: Record<string, CollateralAmountsForm> = {};

    if (!marketInfo) return { mintToInfo: {} };

    for (const asset of marketInfo.marketInformationAssetInfo) {
        mintToInfo[asset.assetIdentifier] = {
            ltvUiAmount: percentDecimalsToUi(asset.ltv),
            liquidationLtvAmount: percentDecimalsToUi(asset.liquidationThreshold),
            marketInformationAddress: marketInfo.marketInformation.address,
            oracleAddress: asset.oracleAccount,
            oracleType: asset.oracleType
        };
    }

    return { mintToInfo };
}

export function buildManagerOverviewForms(
    vaultExpanded: LendingVaultExpanded,
    marketInfo: ParsedMarketInformationWithLtvInfo | undefined,
    strategyDurations: StrategyDuration[] | undefined
) {
    return {
        collateralForm: { ...initialCollateralForm, ...updateCollateralForm(marketInfo) },
        detailForm: getInitialDetailForm(vaultExpanded),
        rateForm: { ...initialRateForm, ...updateRateForm(vaultExpanded, marketInfo, strategyDurations) },
        settingsForm: getInitialSettingsForm(vaultExpanded),
        principalCapsForm: { ...initialPrincipalCapsForm, ...updatePrincipalCaps(vaultExpanded) },
        collateralAllocationForm: {
            ...initialCollateralAllocationForm,
            ...updateCollateralAllocationForm(marketInfo)
        }
    };
}
